import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { LoadingSpinner } from '../../../shared/ui';
import {
  AdminBlogShell,
  AdminListPagination,
  AdminPageHeader,
  usePreservedDomainSearch,
} from './AdminBlogListPage';
import { appendYowanPreviewQueryIfNeeded } from '../../../app/host/yowanPreviewRouting';
import { fetchYowanAdminMailThreadList, YOWAN_CONTENT_ADMIN_PAGE_SIZE } from '../app/yowanContentClient';
const { yowanPageContent } = require('./pageContent');

const statusOptions = [
  { value: '', label: 'すべて' },
  { value: 'open', label: '対応中' },
  { value: 'pending', label: '返信待ち' },
  { value: 'closed', label: '完了' },
  { value: 'archived', label: 'アーカイブ' },
];

const statusLabels = {
  open: '対応中',
  pending: '返信待ち',
  closed: '完了',
  archived: 'アーカイブ',
};

const directionLabels = {
  inbound: '受信',
  outbound: '送信',
};

const statusTone = (thread) => {
  if (thread.needsResponse) {
    return 'review';
  }

  if (thread.status === 'closed' || thread.status === 'archived') {
    return 'draft';
  }

  return 'published';
};

const getUnreadCountLabel = (value) => {
  const count = Number(value || 0);
  if (!Number.isFinite(count) || count <= 0) {
    return '';
  }
  return count > 99 ? '99+' : String(count);
};

const AdminMailThreadsListPage = () => {
  const content = yowanPageContent['/admin/mail'];
  const location = useLocation();
  const preservedDomainSearch = usePreservedDomainSearch();
  const searchParams = new URLSearchParams(location.search);
  const initialQuery = searchParams.get('q') || '';
  const initialStatus = searchParams.get('status') || '';
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [statusFilter, setStatusFilter] = useState(initialStatus);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    setLoadError('');

    fetchYowanAdminMailThreadList({
      page: currentPage,
      pageSize: YOWAN_CONTENT_ADMIN_PAGE_SIZE,
      q: searchQuery.trim(),
      status: statusFilter,
    })
      .then((payload) => {
        if (isMounted) {
          setRows(payload.results);
          setTotalCount(payload.count);
        }
      })
      .catch(() => {
        if (isMounted) {
          setRows([]);
          setTotalCount(0);
          setLoadError(content?.loadErrorMessage || 'メールスレッドを読み込めませんでした。');
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [content?.loadErrorMessage, currentPage, searchQuery, statusFilter]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter]);

  if (!content) {
    return null;
  }

  const totalPages = Math.max(1, Math.ceil(totalCount / YOWAN_CONTENT_ADMIN_PAGE_SIZE));
  const metaText = statusFilter || searchQuery
    ? `${totalCount}件を表示中`
    : `全${totalCount}件`;

  return (
    <AdminBlogShell activeNavHref={content.activeNavHref} routeKey={content.routeKey}>
      <AdminPageHeader
        title={content.header.title}
        paragraphs={content.header.paragraphs}
        actions={[
          <Link
            key="new-mail"
            className="c-form__submit"
            to={appendYowanPreviewQueryIfNeeded('/admin/mail/new', preservedDomainSearch)}
          >
            新規メール
          </Link>,
        ]}
      />

      <div className="yowan-admin-toolbar c-surface-content">
        <div className="yowan-admin-toolbar__group">
          <label className="c-form__field yowan-admin-toolbar__field">
            <span className="c-form__label">検索</span>
            <input
              className="c-form__control yowan-admin-toolbar__search"
              type="search"
              value={searchQuery}
              placeholder={content.searchPlaceholder}
              onChange={(event) => setSearchQuery(event.target.value)}
            />
          </label>
          <label className="c-form__field yowan-admin-toolbar__field">
            <span className="c-form__label">状態</span>
            <select
              className="c-form__control yowan-admin-toolbar__sort"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
            >
              {statusOptions.map((option) => (
                <option key={option.value || 'all'} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </div>
        <p className="yowan-admin-toolbar__meta">{metaText}</p>
      </div>

      {loadError ? <p className="yowan-admin-form__error">{loadError}</p> : null}
      {isLoading ? <LoadingSpinner /> : null}

      <div className="yowan-admin-resource-table-wrap c-surface-content">
        <table className="yowan-admin-resource-table yowan-admin-mail-table">
          <thead>
            <tr>
              <th className="yowan-admin-mail-table__participant">相手</th>
              <th className="yowan-admin-mail-table__subject">件名・最新本文</th>
              <th className="yowan-admin-mail-table__status">状態</th>
              <th className="yowan-admin-mail-table__date">最終更新</th>
              <th className="yowan-admin-mail-table__action">操作</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td>
                  <p className="yowan-admin-resource-table__title">
                    {row.participantName || row.participantEmail || '-'}
                  </p>
                  <p className="yowan-admin-resource-table__subline">{row.participantEmail}</p>
                </td>
                <td>
                  <div className="yowan-admin-mail-table__subject-title">
                    {getUnreadCountLabel(row.unreadCount) ? (
                      <span
                        className="yowan-admin-mail-table__unread-badge"
                        aria-label={`未読${row.unreadCount}通`}
                      >
                        {getUnreadCountLabel(row.unreadCount)}
                      </span>
                    ) : null}
                    <p className="yowan-admin-resource-table__title">{row.subject || '件名なし'}</p>
                  </div>
                  <p className="yowan-admin-resource-table__subline yowan-admin-mail-table__excerpt">
                    {[directionLabels[row.latestDirection] || '', row.latestExcerpt].filter(Boolean).join(' / ') || '-'}
                  </p>
                </td>
                <td>
                  <span className={`yowan-admin-resource-table__status yowan-admin-resource-table__status--${statusTone(row)}`}>
                    {row.needsResponse ? '要返信' : statusLabels[row.status] || row.status || '-'}
                  </span>
                  <p className="yowan-admin-resource-table__subline">
                    {row.messageCount}通{Number(row.unreadCount || 0) > 0 ? ' / 未読あり' : ''}
                  </p>
                </td>
                <td>{row.latestMessageAt}</td>
                <td>
                  <div className="yowan-admin-resource-table__actions">
                    <Link to={appendYowanPreviewQueryIfNeeded(`/admin/mail/threads/${row.id}`, preservedDomainSearch)}>
                      詳細
                    </Link>
                    <Link to={appendYowanPreviewQueryIfNeeded(`/admin/mail/threads/${row.id}/status`, preservedDomainSearch)}>
                      状態変更
                    </Link>
                  </div>
                </td>
              </tr>
            ))}
            {!isLoading && rows.length === 0 ? (
              <tr>
                <td colSpan={5}>{content.emptyMessage}</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <AdminListPagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
    </AdminBlogShell>
  );
};

export default AdminMailThreadsListPage;
